import { observer } from 'mobx-react';
import React from 'react';

import { useStore } from 'utils/hooks/store';

import { ADMIN_AND_ACCOUNT_MANAGERS } from 'utils/roles/roleGroups';

import { UserRole } from 'core/constants';

import TabPanel from 'components/CustomTabPanel/CustomTabPanel';

import { colors } from 'theme/colors';

import { StyledTabs, StyledTab, StyledLock, SectionCard } from './styles';
import { TabsContainer } from 'components/Tabs/styles';

import useMappedFields from '../../hooks/useMappedFields';
import { IdpManagedNote } from '../IdpManaged';
import PersonalInformation from '../PersonalInformation';
import UserJobInformation from '../UserJobInformation';
import UserCustomFields from '../UserCustomFields';
import ProductAccess from '../ProductAccess';
import MedicalInformation from '../MedicalInformation';
import UserGroups from '../UserGroups';

export const UserBackgroundInformation: React.FunctionComponent = () => {
  const { session, userDetails } = useStore();
  const { hasAny, scimEnabled } = useMappedFields();
  // SCIM owns email, phone number, role and licences for the whole org, mapped fields or not.
  const hasMappedFields = hasAny || scimEnabled;
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number): void => {
    setValue(newValue);
  };

  const isSelf = session.user?.uuid === userDetails.details?.uuid;
  // Anyone reads their own groups; otherwise only the roles that can manage groups.
  const showGroupsTab =
    !!session.user?.groupsEnabled && (isSelf || ADMIN_AND_ACCOUNT_MANAGERS.includes(session.user.role));

  return (
    <TabsContainer>
      <>
        <StyledTabs value={value} onChange={handleChange}>
          <StyledTab label="User Record" id="user-record-tab" value={0} />
          {showGroupsTab && <StyledTab label="Groups" id="groups-tab" value={1} />}
          {session.user?.role !== UserRole.User &&
            !(session.user?.uuid === userDetails.details?.uuid && session.user?.role === UserRole.Manager) && (
              <StyledTab label="Product Access" id="product-access-tab" value={2} />
            )}
          <StyledTab
            disabled={session.user?.uuid !== userDetails.details?.uuid}
            label="Medical Information"
            icon={session.user?.uuid !== userDetails.details?.uuid ? <StyledLock /> : undefined}
            iconPosition="end"
            id="medical-information-tab"
            value={3}
          />
        </StyledTabs>

        <TabPanel value={value} index={0}>
          {hasMappedFields && <IdpManagedNote />}
          <SectionCard>
            <PersonalInformation />
          </SectionCard>
          <SectionCard>
            <UserJobInformation />
          </SectionCard>
          {userDetails.details?.hasObjectManager && (
            <SectionCard>
              <UserCustomFields />
            </SectionCard>
          )}
        </TabPanel>
        {showGroupsTab && (
          <TabPanel
            value={value}
            index={1}
            customStyle={{
              backgroundColor: colors.common.white,
              borderRadius: '8px',
              border: `1px solid ${colors.divider}`,
            }}
          >
            <UserGroups />
          </TabPanel>
        )}
        {session.user?.role !== UserRole.User && (
          <TabPanel
            value={value}
            index={2}
            customStyle={{
              backgroundColor: colors.common.white,
              borderRadius: '8px',
              border: `1px solid ${colors.divider}`,
            }}
          >
            <ProductAccess />
          </TabPanel>
        )}
        {session.user?.uuid === userDetails.details?.uuid && (
          <TabPanel
            value={value}
            index={3}
            customStyle={{
              backgroundColor: colors.common.white,
              borderRadius: '8px',
              border: `1px solid ${colors.divider}`,
            }}
          >
            <MedicalInformation />
          </TabPanel>
        )}
      </>
    </TabsContainer>
  );
};

export default observer(UserBackgroundInformation);
